import { Link } from "wouter";
import { ChevronRight } from "lucide-react";
import { CardTitle } from "@/components/ui/card.jsx";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import {
  buildAssetKpiSummaryCells,
  useAssetKpiSummary,
} from "@/hooks/useAssetKpiSummary.js";

/**
 * @param {{
 *   label: string,
 *   value: string|number,
 *   subtitle?: string,
 *   href: string,
 *   linkLabel: string,
 *   testId: string,
 *   isLoading?: boolean,
 * }} props
 * @returns {JSX.Element}
 */
function KpiSummaryCell({
  label,
  value,
  subtitle,
  href,
  linkLabel,
  testId,
  isLoading = false,
}) {
  const link = (
    <Link
      href={href}
      className='ml-auto max-w-full text-right text-sm font-light leading-snug text-brand-cyan hover:text-brand-cyan/90'>
      {linkLabel}
      <ChevronRight className='ml-0.5 inline h-4 w-4 shrink-0 align-text-bottom' />
    </Link>
  );

  return (
    <div
      className='flex h-full min-w-0 flex-col border border-border rounded-md p-3 bg-muted/50'
      data-testid={testId}>
      <p className='mb-1 break-words text-xs font-medium uppercase tracking-wide text-muted-foreground'>
        {label}
      </p>
      {isLoading ? (
        <LoadingSkeleton
          className='h-6 w-16 rounded'
          data-testid={`${testId}-value-loading`}
        />
      ) : (
        <p
          className='text-lg font-semibold text-foreground'
          data-testid={`${testId}-value`}>
          {value}
        </p>
      )}
      <div className='mt-auto flex w-full min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 pt-2'>
        {subtitle ? (
          <p className='min-w-0 text-xs text-muted-foreground'>{subtitle}</p>
        ) : null}
        {link}
      </div>
    </div>
  );
}

/**
 * Compact 30-day KPI strip with drill-down links for Asset Overview.
 * @param {{ compressor: Object, tagData?: Object|null }} props
 * @returns {JSX.Element}
 */
export default function AssetKpiSummary({ compressor, tagData }) {
  const {
    kpiMetrics,
    communicationsAvailabilityDisplay,
    isLoadingComm,
    alarmsSubtitle,
  } = useAssetKpiSummary(compressor, { tagData });

  const cells = buildAssetKpiSummaryCells(
    compressor.id,
    kpiMetrics,
    communicationsAvailabilityDisplay,
    alarmsSubtitle,
    isLoadingComm
  );

  return (
    <div className='border-t border-border pt-4 mt-2'>
      <CardTitle className='mb-3'>Performance Summary</CardTitle>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3'>
        {cells.map((cell) => (
          <KpiSummaryCell key={cell.testId} {...cell} />
        ))}
      </div>
    </div>
  );
}
